(function () {
  const root = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

  function formatMoney(cents) {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents, window.Shopify?.money_format || '${{amount}}');
    }
    return (Number(cents) / 100).toFixed(2);
  }

  function getStrings() {
    const el =
      document.querySelector('[data-cart-page] [data-cart-coupon-strings]') ||
      document.querySelector('[data-cart-coupon-strings]');
    return {
      empty: el?.dataset.strCouponEmpty || 'Please enter a discount code.',
      invalid: el?.dataset.strCouponInvalid || 'This discount code is not valid for your cart.',
      applied: el?.dataset.strCouponApplied || 'Discount applied.',
      removed: el?.dataset.strCouponRemoved || 'Discount removed.',
      removeLabel: el?.dataset.strCouponRemove || 'Remove discount',
    };
  }

  function getAppliedCodes(cart) {
    return (cart?.cart_level_discount_applications || [])
      .map((d) => (d.title || d.code || '').trim())
      .filter(Boolean);
  }

  function codeWasApplied(cart, code) {
    const needle = code.trim().toLowerCase();
    if (!needle) return false;
    return getAppliedCodes(cart).some((c) => c.toLowerCase() === needle);
  }

  function getDiscountCents(cart) {
    if (!cart) return 0;
    if (cart.total_discount > 0) return cart.total_discount;
    return (cart.cart_level_discount_applications || []).reduce(
      (sum, d) => sum + (d.total_allocated_amount || 0),
      0
    );
  }

  async function fetchCart() {
    const res = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Could not load cart');
    return res.json();
  }

  async function postDiscount(discountValue) {
    const res = await fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: discountValue }),
    });
    const data = await res.json();
    if (!res.ok) {
      const msg = data.description || data.message || getStrings().invalid;
      throw new Error(msg);
    }
    return data;
  }

  function showError(wrap, message) {
    if (!wrap) return;
    const el = wrap.querySelector('[data-cart-coupon-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
    wrap.classList.toggle('has-coupon-error', Boolean(message));
  }

  function showSuccess(wrap, message) {
    if (!wrap) return;
    const el = wrap.querySelector('[data-cart-coupon-success]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
    if (message) {
      window.setTimeout(() => {
        el.hidden = true;
        el.textContent = '';
      }, 2800);
    }
  }

  function setCouponBusy(wrap, busy) {
    if (!wrap) return;
    wrap.classList.toggle('is-busy', busy);
    wrap.querySelectorAll('button, input').forEach((node) => {
      if (busy) node.setAttribute('aria-busy', 'true');
      else node.removeAttribute('aria-busy');
    });
  }

  function renderAppliedList(wrap, cart) {
    if (!wrap) return;
    const list = wrap.querySelector('[data-cart-coupon-applied]');
    if (!list) return;
    const codes = getAppliedCodes(cart);
    if (!codes.length) {
      list.innerHTML = '';
      list.hidden = true;
      return;
    }
    list.hidden = false;
    list.innerHTML = codes
      .map(
        (code) =>
          `<li class="cart-coupon__applied-item">
            <span class="cart-coupon__applied-code">${escapeHtml(code)}</span>
            <button type="button" class="cart-coupon__remove" data-cart-coupon-remove data-code="${escapeHtml(code)}" aria-label="${escapeHtml(getStrings().removeLabel)} ${escapeHtml(code)}">
              <span aria-hidden="true">&times;</span>
            </button>
          </li>`
      )
      .join('');
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function updateFromCart(cart) {
    if (!cart) return;

    const discountCents = getDiscountCents(cart);
    const discountMoney = discountCents > 0 ? `-${formatMoney(discountCents)}` : '';

    document.querySelectorAll('[data-cart-discount]').forEach((el) => {
      el.textContent = discountMoney;
    });

    document.querySelectorAll('[data-cart-discount-row]').forEach((row) => {
      if (discountCents > 0) row.removeAttribute('hidden');
      else row.setAttribute('hidden', '');
    });

    document.querySelectorAll('[data-cart-subtotal]').forEach((el) => {
      el.textContent = formatMoney(cart.items_subtotal_price || 0);
    });

    document.querySelectorAll('[data-cart-total]').forEach((el) => {
      el.textContent = formatMoney(cart.total_price || 0);
    });

    document.querySelectorAll('[data-cart-coupon-wrap]').forEach((wrap) => {
      renderAppliedList(wrap, cart);
      const input = wrap.querySelector('[name="discount"]');
      if (input && getAppliedCodes(cart).length) input.value = '';
    });
  }

  async function applyCode(code) {
    const trimmed = code.trim();
    if (!trimmed) throw new Error(getStrings().empty);

    const before = await fetchCart();
    const beforeDiscount = getDiscountCents(before);
    const existing = getAppliedCodes(before);
    const codes = existing.some((c) => c.toLowerCase() === trimmed.toLowerCase())
      ? existing
      : [...existing, trimmed];
    const cart = await postDiscount(codes.join(','));

    if (codeWasApplied(cart, trimmed) || getDiscountCents(cart) > beforeDiscount) {
      return cart;
    }
    throw new Error(getStrings().invalid);
  }

  async function removeCode(code) {
    const cart = await fetchCart();
    const remaining = getAppliedCodes(cart).filter((c) => c.toLowerCase() !== code.trim().toLowerCase());
    return postDiscount(remaining.join(','));
  }

  async function handleApply(form) {
    const wrap = form.closest('[data-cart-coupon-wrap]');
    const input = form.querySelector('[name="discount"]');
    const code = input?.value?.trim() || '';
    showError(wrap, '');
    showSuccess(wrap, '');
    setCouponBusy(wrap, true);
    try {
      const cart = await applyCode(code);
      updateFromCart(cart);
      if (window.Track50CartShipping) window.Track50CartShipping.updateAll(cart);
      document.dispatchEvent(new CustomEvent('track50:cart:refresh'));
      showSuccess(wrap, getStrings().applied);
      if (input) input.value = '';
      const toggle = wrap?.querySelector('[data-cart-coupon-toggle]');
      const couponForm = wrap?.querySelector('[data-cart-coupon-form]');
      if (toggle && couponForm && !couponForm.hasAttribute('hidden')) {
        couponForm.setAttribute('hidden', '');
        toggle.setAttribute('aria-expanded', 'false');
      }
    } catch (err) {
      showError(wrap, err.message || getStrings().invalid);
    } finally {
      setCouponBusy(wrap, false);
    }
  }

  async function handleRemove(btn) {
    const wrap = btn.closest('[data-cart-coupon-wrap]');
    const code = btn.dataset.code || '';
    if (!code) return;
    showError(wrap, '');
    setCouponBusy(wrap, true);
    try {
      const cart = await removeCode(code);
      updateFromCart(cart);
      if (window.Track50CartShipping) window.Track50CartShipping.updateAll(cart);
      document.dispatchEvent(new CustomEvent('track50:cart:refresh'));
      showSuccess(wrap, getStrings().removed);
    } catch (err) {
      showError(wrap, err.message || getStrings().invalid);
    } finally {
      setCouponBusy(wrap, false);
    }
  }

  function bindEvents() {
    if (document.documentElement.dataset.cartCouponBound === 'true') return;
    document.documentElement.dataset.cartCouponBound = 'true';

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-cart-coupon-form]');
      if (!form) return;
      event.preventDefault();
      handleApply(form);
    });

    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-cart-coupon-toggle]');
      if (toggle) {
        event.preventDefault();
        const wrap = toggle.closest('[data-cart-coupon-wrap]');
        const form = wrap?.querySelector('[data-cart-coupon-form]');
        if (!form) return;
        const open = form.hasAttribute('hidden');
        if (open) form.removeAttribute('hidden');
        else form.setAttribute('hidden', '');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) form.querySelector('[name="discount"]')?.focus();
        return;
      }

      const removeBtn = event.target.closest('[data-cart-coupon-remove]');
      if (removeBtn) {
        event.preventDefault();
        handleRemove(removeBtn);
      }
    });

    document.addEventListener('track50:cart:refresh', () => {
      fetchCart()
        .then((cart) => {
          updateFromCart(cart);
        })
        .catch(() => {});
    });
  }

  window.Track50CartDiscount = {
    applyCode,
    removeCode,
    updateFromCart,
    fetchCart,
  };

  bindEvents();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      fetchCart().then(updateFromCart).catch(() => {});
    });
  } else {
    fetchCart().then(updateFromCart).catch(() => {});
  }
})();
